/**
 * Client-side OOXML property fragments → CSS, the display half of the
 * Studio's property-passthrough model: the backend keeps pPr/rPr/tcPr XML
 * verbatim on the Tiptap nodes, and the canvas derives its presentation from
 * those attrs here. Nothing in this file feeds the compiler — attrs are
 * never modified, only read. The editor's formatting deltas (RED-317 PR 3
 * contract, .claude/tickets/RED-317/pr3-contract.md §1, §2, §4: run colour
 * and highlight, paragraph alignment, cell fill) ride NEXT to the verbatim
 * fragment, and the *ToCss functions render them the way the compiler
 * writes them: the delta replaces only its own property.
 *
 * Guarded for non-DOM runtimes: parsing is lazy (node --test injects a DOM
 * via linkedom), every parse AND every derived CSS string is memoized by
 * fragment string, and the caches are bounded so a long editing session
 * cannot grow them without limit.
 */

import { fontFamilyCss } from './fontStack.js'
import { fontMetricsCss, lineSpacingCss, paragraphMarkCss, themeFontCss, vertAlignCss } from './lineMetrics.js'

const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main'

const CACHE_MAX = 2000

const parseCache = new Map()
const rPrCache = new Map()
const pPrCache = new Map()
const tcPrCache = new Map()
const tblPrCache = new Map()
const tblGridCache = new Map()

let domParser = null

/** Insertion-order-bounded memo: evicts the oldest entry past CACHE_MAX. */
function memo(cache, key, compute) {
  const cached = cache.get(key)
  if (cached !== undefined) return cached
  const value = compute()
  if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value)
  cache.set(key, value)
  return value
}

const HIGHLIGHT_COLORS = {
  yellow: '#ffff00',
  green: '#00ff00',
  cyan: '#00ffff',
  magenta: '#ff00ff',
  blue: '#0000ff',
  red: '#ff0000',
  darkBlue: '#00008b',
  darkCyan: '#008b8b',
  darkGreen: '#006400',
  darkMagenta: '#8b008b',
  darkRed: '#8b0000',
  darkYellow: '#808000',
  darkGray: '#a9a9a9',
  lightGray: '#d3d3d3',
  black: '#000000',
  white: '#ffffff',
}

const BORDER_STYLES = {
  single: 'solid',
  thick: 'solid',
  double: 'double',
  dotted: 'dotted',
  dashed: 'dashed',
  dotDash: 'dashed',
  dotDotDash: 'dashed',
  wave: 'solid',
  none: 'none',
  nil: 'none',
}

/** w:u val → text-decoration-style for the non-plain underline variants. */
const UNDERLINE_STYLES = {
  double: 'double',
  dotted: 'dotted',
  dottedHeavy: 'dotted',
  dash: 'dashed',
  dashedHeavy: 'dashed',
  dashLong: 'dashed',
  dashLongHeavy: 'dashed',
  dotDash: 'dashed',
  dashDotHeavy: 'dashed',
  dotDotDash: 'dashed',
  dashDotDotHeavy: 'dashed',
  wave: 'wavy',
  wavyHeavy: 'wavy',
  wavyDouble: 'wavy',
  thick: 'solid',
  words: 'solid',
}

/** Parse a namespace-prefixed OOXML fragment into its root element, memoized. */
function parseFragment(xml) {
  if (!xml || typeof xml !== 'string') return null
  return memo(parseCache, xml, () => {
    try {
      domParser ??= new DOMParser()
      const wrapped = `<w:root xmlns:w="${W_NS}">${xml}</w:root>`
      const doc = domParser.parseFromString(wrapped, 'text/xml')
      return doc.querySelector('parsererror') ? null : doc.documentElement.firstElementChild
    } catch {
      return null
    }
  })
}

function children(element) {
  return element ? Array.from(element.children) : []
}

function attr(element, name) {
  return element.getAttributeNS(W_NS, name) || element.getAttribute(`w:${name}`) || ''
}

/**
 * Element local name with any namespace prefix stripped — browser DOMParser
 * already strips it for namespaced XML; linkedom (node --test) does not.
 */
function local(element) {
  const name = element.localName
  const colon = name.indexOf(':')
  return colon === -1 ? name : name.slice(colon + 1)
}

function isOn(value) {
  return value === '1' || value === 'true' || value === 'on'
}

function isOff(value) {
  return ['0', 'false', 'none', 'off'].includes(value.toLowerCase())
}

const twipsToPt = (twips) => Number(twips) / 20

const hex = (value) => (/^[0-9A-Fa-f]{6}$/.test(value) ? `#${value}` : null)

/**
 * Word's automatic text colour as CSS: black, white on a dark fill. A filled
 * cell, paragraph or run publishes the colour as `--studio-auto-color`
 * (studio.css, the data-fill-tone rules; a table style's region fill:
 * studioStylesheets.js), so explicit "auto" follows the fill like text that
 * names no colour at all.
 */
export const AUTO_COLOR_CSS = 'color: var(--studio-auto-color, #000)'

/**
 * A w:color value as CSS. Explicit "auto" is the author RESETTING an
 * inherited color (e.g. a table style's white first-row text) to Word's
 * automatic colour (AUTO_COLOR_CSS). Emitting nothing would let the style
 * color win.
 */
function colorCss(value) {
  const color = hex(value)
  if (color) return `color: ${color}`
  return value === 'auto' ? AUTO_COLOR_CSS : null
}

/** Run highlight names the editor may set (contract §1); 'none' removes the highlight. */
export const RUN_HIGHLIGHTS = Object.freeze(Object.keys(HIGHLIGHT_COLORS))

/** The CSS colour Word paints each highlight name in (the toolbar's swatches). */
export const RUN_HIGHLIGHT_COLORS = Object.freeze({ ...HIGHLIGHT_COLORS })

/** The text colours the toolbar offers (contract §1); the compiler takes any 6-hex or 'auto'. */
export const RUN_COLORS = Object.freeze(['auto', 'FF671F', 'FF0000', '0000FF', 'BFBFBF'])

/** Paragraph alignments the editor may set (contract §2) → CSS text-align. */
export const PARAGRAPH_ALIGNMENTS = Object.freeze(['left', 'center', 'right', 'justify'])

/** w:jc value → the alignment it shows as (start/end/distribute fold in, contract §2). */
const JC_ALIGNMENTS = {
  start: 'left',
  left: 'left',
  center: 'center',
  end: 'right',
  right: 'right',
  both: 'justify',
  distribute: 'justify',
}

/** A colour delta the compiler accepts: 'auto' or six hex digits, upper-cased; else null. */
export function normalizeRunColor(value) {
  if (value === 'auto') return 'auto'
  return typeof value === 'string' && /^[0-9A-Fa-f]{6}$/.test(value) ? value.toUpperCase() : null
}

/** Memo key of a fragment plus its deltas; the bare fragment when there are none. */
function deltaKey(fragment, deltas) {
  return deltas.every((delta) => delta === null) ? fragment : [fragment ?? '', ...deltas.map((delta) => delta ?? '')].join('\u0000')
}

/**
 * Run-level CSS. Bold/italic/underline/strike are first-class Tiptap marks
 * rendered by their own extensions — here we surface the properties that
 * stay inside the rPr residual: font (local-first stack), size, color,
 * highlight/shading, caps, letter spacing, raised/lowered position,
 * non-plain underline styles, double strike, vertical alignment.
 *
 * `deltas` are the ooxmlRun mark's colour/highlight attrs (contract §1): a
 * colour replaces any w:color, a highlight name replaces w:highlight, and
 * 'none' drops it — the run's own w:shd then shows again, as in Word. The
 * highlight is emitted last: Word paints it over the run shading.
 */
export function rPrToCss(rPr, deltas = {}) {
  const colorDelta = deltas.color ?? null
  const highlightDelta = deltas.highlight ?? null
  const root = parseFragment(rPr)
  if (!root && colorDelta === null && highlightDelta === null) return ''

  return memo(rPrCache, deltaKey(rPr, [colorDelta, highlightDelta]), () => {
    const css = []
    let highlight = null
    let sizePt = null
    let script = null

    for (const property of children(root)) {
      const value = attr(property, 'val')
      switch (local(property)) {
        case 'rFonts': {
          const stack = fontFamilyCss(attr(property, 'ascii'))
          if (stack) css.push(`font-family: ${stack}`, fontMetricsCss(attr(property, 'ascii')))
          else if (attr(property, 'asciiTheme')) css.push(themeFontCss(attr(property, 'asciiTheme')))
          break
        }
        case 'sz':
          if (Number(value) > 0) sizePt = Number(value) / 2
          break
        case 'color': {
          const color = colorDelta === null ? colorCss(value) : null
          if (color) css.push(color)
          break
        }
        case 'highlight':
          if (highlightDelta === null && HIGHLIGHT_COLORS[value]) highlight = HIGHLIGHT_COLORS[value]
          break
        case 'shd': {
          const fill = hex(attr(property, 'fill'))
          if (fill) css.push(`background-color: ${fill}`)
          break
        }
        case 'caps':
          if (!isOff(value)) css.push('text-transform: uppercase')
          break
        case 'smallCaps':
          if (!isOff(value)) css.push('font-variant: small-caps')
          break
        case 'spacing': {
          // Character spacing, twips (may be negative).
          const amount = Number(value)
          if (amount !== 0 && Number.isFinite(amount)) css.push(`letter-spacing: ${twipsToPt(amount)}pt`)
          break
        }
        case 'position': {
          // Raised/lowered text, half-points (positive = raised), on top of
          // the run's own baseline placement (studio.css .studio-run).
          const amount = Number(value)
          if (amount !== 0 && Number.isFinite(amount)) css.push(`--studio-raise: ${amount / 2}pt`)
          break
        }
        case 'u': {
          // val="single" is drawn by the <u> mark element; only the fancy
          // variants need an explicit decoration (the mark's plain line
          // overlaps it — an accepted v1 artifact).
          const style = UNDERLINE_STYLES[value]
          const color = hex(attr(property, 'color'))
          if (style && style !== 'solid') {
            css.push(`text-decoration: underline ${style}${color ? ' ' + color : ''}`)
          } else if (color && value && !isOff(value)) {
            css.push(`text-decoration: underline ${color}`)
          }
          break
        }
        case 'dstrike':
          if (!isOff(value)) css.push('text-decoration: line-through double')
          break
        case 'vertAlign':
          script = vertAlignCss(value, null) ? value : null
          break
        case 'vanish':
          if (!isOff(value)) css.push('display: none')
          break
      }
    }

    // Size after the loop: a superscript shrinks the run's own w:sz (lineMetrics.js).
    if (script) css.push(vertAlignCss(script, sizePt))
    else if (sizePt) css.push(`font-size: ${sizePt}pt`)

    if (colorDelta !== null) {
      const color = colorCss(colorDelta)
      if (color) css.push(color)
    }
    if (highlightDelta !== null) highlight = HIGHLIGHT_COLORS[highlightDelta] ?? null
    if (highlight) css.push(`background-color: ${highlight}`)

    return css.join('; ')
  })
}

/**
 * Paragraph-level CSS: alignment, indentation (as margins, matching Word's
 * box model for shading/borders), spacing incl. line height, paragraph
 * borders and shading. `deltas.textAlign` (contract §2) replaces w:jc.
 */
export function pPrToCss(pPr, deltas = {}) {
  const alignDelta = PARAGRAPH_ALIGNMENTS.includes(deltas.textAlign) ? deltas.textAlign : null
  const root = parseFragment(pPr)
  if (!root && alignDelta === null) return ''

  return memo(pPrCache, deltaKey(pPr, [alignDelta]), () => {
    const css = []

    for (const property of children(root)) {
      switch (local(property)) {
        case 'jc': {
          const align = alignDelta === null ? JC_ALIGNMENTS[attr(property, 'val')] : null
          if (align) css.push(`text-align: ${align}`)
          break
        }
        case 'rPr': {
          // The paragraph mark: what an empty paragraph's height comes from.
          const fonts = children(property).find((child) => local(child) === 'rFonts')
          const size = children(property).find((child) => local(child) === 'sz')
          const mark = paragraphMarkCss(fonts ? attr(fonts, 'ascii') : null, size ? Number(attr(size, 'val')) / 2 : null)
          if (mark) css.push(mark)
          break
        }
        case 'ind': {
          const left = Number(attr(property, 'left') || attr(property, 'start') || 0)
          const right = Number(attr(property, 'right') || attr(property, 'end') || 0)
          const hanging = Number(attr(property, 'hanging') || 0)
          const firstLine = Number(attr(property, 'firstLine') || 0)
          if (left > 0) css.push(`margin-left: ${twipsToPt(left)}pt`)
          if (right > 0) css.push(`margin-right: ${twipsToPt(right)}pt`)
          if (hanging > 0) css.push(`text-indent: -${twipsToPt(hanging)}pt`)
          else if (firstLine > 0) css.push(`text-indent: ${twipsToPt(firstLine)}pt`)
          break
        }
        case 'spacing': {
          // Word's HTML auto spacing replaces before/after with 14 pt.
          const before = isOn(attr(property, 'beforeAutospacing')) ? '280' : attr(property, 'before')
          const after = isOn(attr(property, 'afterAutospacing')) ? '280' : attr(property, 'after')
          const line = Number(attr(property, 'line') || 0)
          const lineRule = attr(property, 'lineRule')
          if (before !== '') css.push(`margin-top: ${twipsToPt(Number(before))}pt`)
          if (after !== '') css.push(`margin-bottom: ${twipsToPt(Number(after))}pt`)
          // Word's line model (lineMetrics.js): studio.css derives the height and baseline.
          const lineCss = lineSpacingCss(line, lineRule)
          if (lineCss) css.push(lineCss)
          break
        }
        case 'pBdr':
          for (const edge of children(property)) {
            if (!['top', 'left', 'bottom', 'right'].includes(local(edge))) continue
            const border = borderCss(edge)
            if (border === 'none') continue
            css.push(`border-${local(edge)}: ${border}`)
            const space = Number(attr(edge, 'space') || 0)
            if (space > 0) css.push(`padding-${local(edge)}: ${space}pt`)
          }
          break
        case 'shd': {
          const fill = hex(attr(property, 'fill'))
          if (fill) css.push(`background-color: ${fill}`)
          break
        }
        case 'tabs': {
          // Serialize the stops into a custom property so the tab layout
          // engine can read them back from ANY rendered DOM — the editable
          // canvas and the static header/footer chrome alike.
          const stops = []
          for (const stop of children(property)) {
            if (local(stop) !== 'tab') continue
            const val = attr(stop, 'val') || 'left'
            const pos = Number(attr(stop, 'pos'))
            if (val === 'clear' || !Number.isFinite(pos)) continue
            stops.push(`${twipsToPt(pos)} ${val} ${attr(stop, 'leader') || 'none'}`)
          }
          if (stops.length) css.push(`--studio-tabs: ${stops.join(', ')}`)
          break
        }
      }
    }

    if (alignDelta !== null) css.push(`text-align: ${alignDelta}`)

    return css.join('; ')
  })
}

/**
 * Parse a --studio-tabs custom-property value back into stop objects.
 * Inverse of the serialization above; exported for the tab layout engine.
 */
export function parseTabStops(value) {
  if (!value || typeof value !== 'string') return []
  const stops = []
  for (const entry of value.split(',')) {
    const [pos, val = 'left', leader = 'none'] = entry.trim().split(/\s+/)
    const posPt = Number(pos)
    if (Number.isFinite(posPt)) stops.push({ posPt, val, leader })
  }
  return stops.sort((a, b) => a.posPt - b.posPt)
}

function borderCss(borderElement) {
  const style = BORDER_STYLES[attr(borderElement, 'val')] || 'solid'
  if (style === 'none') return 'none'
  const size = Number(attr(borderElement, 'sz') || 4) / 8
  // Word's border color "auto" is BLACK — borders never inherit the text
  // color. currentColor would paint white (invisible) borders inside
  // table-style cells whose text color is white.
  const color = hex(attr(borderElement, 'color')) || 'rgb(0, 0, 0)'
  return `${Math.max(size, 0.5)}pt ${style} ${color}`
}

/**
 * Whether a tcBorders edge overrides the table's border for that side.
 * Measured in Word.app 16.112 (RED-317 PR 3c-E): `nil`, and `none` with a
 * width, switch the line off — on an outer edge too, and where both cells
 * say so; `none` without a width (bare, or sz="0" as Word writes it) is no
 * override, and the table's border draws.
 */
function overridesBorder(borderElement) {
  return attr(borderElement, 'val') !== 'none' || Number(attr(borderElement, 'sz') || 0) > 0
}

/**
 * Cell-level CSS: width, shading (cellShading), explicit borders, cell
 * margins. `deltas.fill` (contract §4) replaces the cell shading.
 * `deltas.shading: false` leaves the shading out: a vMerge continuation draws
 * the shading of the cell it continues (tableRegions.js).
 */
export function tcPrToCss(tcPr, deltas = {}) {
  const fillDelta = normalizeRunColor(deltas.fill ?? null)
  const shading = deltas.shading !== false
  const root = parseFragment(tcPr)
  if (!root && (fillDelta === null || !shading)) return ''

  return memo(tcPrCache, deltaKey(tcPr, [fillDelta, shading ? null : 'unshaded']), () => {
    const css = []

    for (const property of children(root)) {
      switch (local(property)) {
        case 'tcW': {
          const width = Number(attr(property, 'w'))
          const type = attr(property, 'type')
          if (width > 0 && (type === 'dxa' || type === '')) css.push(`width: ${twipsToPt(width)}pt`)
          else if (width > 0 && type === 'pct') css.push(`width: ${width / 50}%`)
          break
        }
        case 'tcBorders':
          for (const edge of children(property)) {
            if (['top', 'left', 'bottom', 'right'].includes(local(edge)) && overridesBorder(edge)) {
              // An explicit "no line" is CSS 'none': where two cells meet
              // the neighbour's border wins the collapse, as in Word (the
              // Eesti Pank table of every §4.1 mall); on the table's outer
              // edge nothing else draws it (studio.css puts the outer
              // borders on the edge cells, tableRegions.js).
              css.push(`border-${local(edge)}: ${borderCss(edge)}`)
            }
          }
          break
        case 'tcMar':
          for (const edge of children(property)) {
            if (!['top', 'left', 'bottom', 'right'].includes(local(edge))) continue
            const width = Number(attr(edge, 'w'))
            if (attr(edge, 'type') === 'dxa' && width >= 0) {
              css.push(`padding-${local(edge)}: ${twipsToPt(width)}pt`)
            }
          }
          break
        case 'vAlign': {
          const value = attr(property, 'val')
          const align = { top: 'top', center: 'middle', bottom: 'bottom' }[value]
          if (align) css.push(`vertical-align: ${align}`)
          break
        }
      }
    }

    const background = shading ? cellShading(tcPr, fillDelta).css : ''
    if (background) css.push(background)

    return css.join('; ')
  })
}

const NO_SHADING = Object.freeze({ css: '', tone: null })

const shadingCache = new Map()

/**
 * A cell's shading as Word paints it: one background declaration ('' when
 * the cell has none of its own) and the tone of that background for Word's
 * automatic text colour (fillTone; a cleared fill shows the white page,
 * 'light'). The fill delta (contract §4) replaces the w:shd: a 6-hex fill
 * paints it, 'auto' clears a fill the tcPr had (the compiler writes an
 * explicit w:fill="auto", which wins over a table style's fill in Word too).
 * A direct "no shading" (w:shd fill auto) beats a table style's region fill
 * in Word (the Võrdlustehingute table of the korteriomand malls).
 *
 * @returns {{ css: string, tone: 'dark'|'light'|null }}
 */
export function cellShading(tcPr, fill = null) {
  const fillDelta = normalizeRunColor(fill ?? null)
  const root = parseFragment(tcPr)
  if (!root && fillDelta === null) return NO_SHADING

  return memo(shadingCache, deltaKey(tcPr, [fillDelta]), () => {
    const shd = children(root).find((property) => local(property) === 'shd') ?? null
    const own = shd ? hex(attr(shd, 'fill')) : null
    if (fillDelta === 'auto') return own ? { css: 'background-color: transparent', tone: 'light' } : NO_SHADING
    if (fillDelta !== null) return { css: `background-color: #${fillDelta}`, tone: fillTone(fillDelta) }
    if (own) return { css: `background-color: ${own}`, tone: fillTone(own) }
    if (shd && ['clear', 'nil', 'none'].includes(attr(shd, 'val'))) return { css: 'background-color: transparent', tone: 'light' }
    return NO_SHADING
  })
}

/**
 * The tone of a fill for Word's automatic text colour, which turns white on
 * a dark fill: Rec.601 luma under 75. Measured in Word.app 16.112 (compat
 * mode 15): white on 4A4A4A, 9000FF, 007C00 (luma ≤ 74), black on 4C4C4C,
 * 008000, FF0000, A000FF, 007070 (luma ≥ 75.1). 'dark' or 'light' for a
 * 6-hex fill, with or without '#'; null for anything else.
 */
export function fillTone(value) {
  const match = /^#?([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})$/.exec(typeof value === 'string' ? value : '')
  if (!match) return null
  const [red, green, blue] = match.slice(1).map((part) => parseInt(part, 16))
  return 0.299 * red + 0.587 * green + 0.114 * blue < 75 ? 'dark' : 'light'
}

const shadingToneCache = new Map()

/**
 * The tone of the fill a pPr's or rPr's own w:shd paints (fillTone), or null
 * without one: Word's automatic text colour turns white on dark paragraph
 * and run shading too (not on a highlight). Published as data-fill-tone.
 */
export function shadingTone(fragment) {
  const root = parseFragment(fragment)
  if (!root) return null
  return memo(shadingToneCache, fragment, () => {
    const shd = children(root).find((property) => local(property) === 'shd')
    return shd ? fillTone(attr(shd, 'fill')) : null
  })
}

/**
 * Table-level presentation as CSS custom properties consumed by the
 * studio.css table rules: per-edge outer borders (--sb-top/right/bottom/
 * left, drawn on the table box and merged by border-collapse), inside
 * borders (--sb-insideH/V, the default for every cell edge), default cell
 * padding (--sb-pad-*), plus table width/alignment/indent and shading.
 * Per-cell tcPr styles override inline as before.
 */
export function tblPrToCss(tblPr) {
  const root = parseFragment(tblPr)
  if (!root) return ''

  return memo(tblPrCache, tblPr, () => {
    const css = []

    for (const property of children(root)) {
      switch (local(property)) {
        case 'tblBorders':
          for (const edge of children(property)) {
            const name = { top: '--sb-top', right: '--sb-right', bottom: '--sb-bottom', left: '--sb-left', insideH: '--sb-insideH', insideV: '--sb-insideV' }[local(edge)]
            if (!name) continue
            // 'none'/'nil' is a REAL value: the instance is switching a
            // table-style border OFF for this edge (Word semantics). Only
            // ABSENT edges inherit from the bound style's custom property.
            css.push(`${name}: ${borderCss(edge)}`)
          }
          break
        case 'tblW': {
          const width = Number(attr(property, 'w'))
          const type = attr(property, 'type')
          if (width > 0 && type === 'dxa') css.push(`--sb-width: ${twipsToPt(width)}pt`)
          else if (width > 0 && type === 'pct') css.push(`--sb-width: ${width / 50}%`)
          break
        }
        case 'tblInd': {
          const width = Number(attr(property, 'w'))
          if (attr(property, 'type') === 'dxa' && width > 0) css.push(`margin-left: ${twipsToPt(width)}pt`)
          break
        }
        case 'jc': {
          const value = attr(property, 'val')
          if (value === 'center') css.push('margin-left: auto', 'margin-right: auto')
          if (value === 'right' || value === 'end') css.push('margin-left: auto')
          break
        }
        case 'tblCellMar':
          for (const edge of children(property)) {
            if (!['top', 'left', 'bottom', 'right'].includes(local(edge))) continue
            const width = Number(attr(edge, 'w'))
            if (attr(edge, 'type') === 'dxa' && width >= 0) {
              css.push(`--sb-pad-${local(edge)}: ${twipsToPt(width)}pt`)
            }
          }
          break
        case 'shd': {
          const fill = hex(attr(property, 'fill'))
          if (fill) css.push(`background-color: ${fill}`)
          break
        }
        case 'tblpPr':
          css.push(floatingTableCss(tblpPrValues(property)))
          break
      }
    }

    return css.join('; ')
  })
}

const tcEdgesCache = new Map()

/**
 * The edges a tcPr's w:tcBorders names, whatever it sets them to, as a
 * space-separated list ('' when none) for data-tc-edges. Measured in
 * Word.app 16.112: a table style's region border beats the table's own
 * tblBorders, but a cell edge the cell names itself replaces the region's
 * edge, even `none sz=0` (which then leaves the table's border to draw, as
 * overridesBorder says); the malls' styled tables name their edges this way.
 */
export function tcBorderEdges(tcPr) {
  const root = parseFragment(tcPr)
  if (!root) return ''
  return memo(tcEdgesCache, tcPr, () => {
    const borders = children(root).find((property) => local(property) === 'tcBorders')
    const named = new Set(children(borders).map((edge) => local(edge)))
    return ['top', 'left', 'bottom', 'right'].filter((edge) => named.has(edge)).join(' ')
  })
}

const floatingCache = new Map()

/** True when a tblPr makes the table float (w:tblpPr): text wraps around it. */
export function isFloatingTable(tblPr) {
  const root = parseFragment(tblPr)
  if (!root) return false
  return memo(floatingCache, tblPr, () => children(root).some((property) => local(property) === 'tblpPr'))
}

/**
 * w:tblpPr attributes (ECMA-376 §17.4.57): distances and offsets in twips,
 * anchors and alignments as written. Absent anchors take Word's defaults
 * (horizontal: the text column, vertical: the margin).
 */
function tblpPrValues(element) {
  const twips = (name) => {
    const value = Number(attr(element, name))
    return Number.isFinite(value) ? value : 0
  }
  return {
    leftFromText: twips('leftFromText'),
    rightFromText: twips('rightFromText'),
    bottomFromText: twips('bottomFromText'),
    horzAnchor: attr(element, 'horzAnchor') || 'text',
    vertAnchor: attr(element, 'vertAnchor') || 'margin',
    tblpX: twips('tblpX'),
    tblpXSpec: attr(element, 'tblpXSpec') || null,
    tblpY: twips('tblpY'),
    tblpYSpec: attr(element, 'tblpYSpec') || null,
  }
}

/**
 * A floating table's position as CSS: a float on the side Word puts it, its
 * distances from the text as margins on the sides text flows along (the side
 * against the margin keeps none, as in Word). Horizontal offsets count from
 * the text column; a page-relative one counts from the page's left edge, so
 * the column-relative position is the offset less the left page margin
 * (`--studio-margin-left`, published by buildPresentationCss; measured in
 * Word.app 16.112: page tblpX 2400 = text tblpX 983 under a 1417 margin).
 * Word also hangs the table's border out by the cell margin (the border at
 * tblpX − 108 tw); the canvas puts the border at tblpX. Vertically
 * only a text-relative offset is honoured — margin or page positions are page
 * coordinates the continuous canvas cannot place, so the table starts where
 * its anchor paragraph does. A centred table cannot have text on both sides
 * in CSS: it centres in the flow.
 *
 * @param {{leftFromText: number, rightFromText: number, bottomFromText: number,
 *   horzAnchor: string, vertAnchor: string, tblpX: number, tblpXSpec: string|null,
 *   tblpY: number, tblpYSpec: string|null}} position twips
 */
export function floatingTableCss(position) {
  const pt = (twips) => `${twipsToPt(twips)}pt`
  const top = position.vertAnchor === 'text' && !position.tblpYSpec ? position.tblpY : 0
  const bottom = position.bottomFromText

  if (position.tblpXSpec === 'center') {
    return `margin: ${pt(top)} auto ${pt(bottom)}`
  }
  if (position.tblpXSpec === 'right' || position.tblpXSpec === 'outside') {
    return `float: right; margin: ${pt(top)} 0 ${pt(bottom)} ${pt(position.leftFromText)}`
  }
  const left = position.tblpXSpec ? pt(0) : position.horzAnchor === 'page' ? `calc(${pt(position.tblpX)} - var(--studio-margin-left, 0pt))` : pt(position.tblpX)
  return `float: left; margin: ${pt(top)} ${pt(position.rightFromText)} ${pt(bottom)} ${left}`
}

/** Points rounded to 1/100 pt, as CSS. */
const ptCss = (points) => `${Math.round(points * 100) / 100}pt`

/**
 * Where an anchored Word object sits (the importer's rawRun box projection,
 * points), as CSS for an element inside its anchor paragraph — the text box
 * counterpart of the picture rules in studioNodes.js: a left/right-aligned
 * square or tight wrap floats with its text distances on the wrapped sides,
 * an absolute horizontal offset floats left from the column, a paragraph-
 * relative vertical offset moves it down; no wrap (in front of or behind the
 * text) positions it absolutely in the paragraph; top-and-bottom (and a
 * centred square wrap, which CSS cannot float) is a block of its own. An
 * inline object stays in the line.
 *
 * @returns {{css: string, absolute: boolean}}
 */
function objectPlacementCss(box) {
  if (!box.anchored) return { css: 'display: inline-flex; vertical-align: bottom', absolute: false }

  const dist = box.distPt ?? { t: 0, r: 0, b: 0, l: 0 }
  const posH = box.positionH ?? {}
  const posV = box.positionV ?? {}
  const offsetV = ['paragraph', 'line'].includes(posV.relativeFrom) && posV.offsetPt !== null ? posV.offsetPt : 0

  if (box.wrap === 'none') {
    const css = ['position: absolute', `top: ${ptCss(offsetV)}`]
    if (posH.align === 'right') css.push('right: 0')
    else if (posH.align === 'center') css.push('left: 50%', 'transform: translateX(-50%)')
    else css.push(`left: ${ptCss(posH.offsetPt ?? 0)}`)
    if (box.behindDoc) css.push('z-index: -1')
    return { css: css.join('; '), absolute: true }
  }

  const top = ptCss(dist.t + offsetV)
  const wraps = box.wrap === 'square' || box.wrap === 'tight'
  if (wraps && posH.align === 'right') {
    return { css: `float: right; margin: ${top} 0 ${ptCss(dist.b)} ${ptCss(dist.l)}`, absolute: false }
  }
  if (wraps && posH.align !== 'center') {
    const left = posH.align === 'left' ? 0 : (posH.offsetPt ?? 0)
    return { css: `float: left; margin: ${top} ${ptCss(dist.r)} ${ptCss(dist.b)} ${ptCss(left)}`, absolute: false }
  }

  const left = posH.align === 'right' || posH.align === 'center' ? 'auto' : ptCss(posH.align === 'left' ? 0 : (posH.offsetPt ?? 0))
  const right = posH.align === 'right' ? '0' : 'auto'
  return { css: `display: flex; clear: both; margin: ${top} ${right} ${ptCss(dist.b)} ${left}`, absolute: false }
}

/**
 * A Word text box's frame (rawRun box projection, points) as CSS: its size
 * (the extent, insets inside it), fill, outline, where its text starts
 * vertically, and where it sits in its paragraph (objectPlacementCss).
 *
 * @returns {{css: string, absolute: boolean}}
 */
export function textBoxCss(box) {
  if (!box) return { css: '', absolute: false }
  const placement = objectPlacementCss(box)
  const css = [placement.css]
  if (box.widthPt > 0) css.push(`width: ${ptCss(box.widthPt)}`)
  if (box.heightPt > 0) css.push(`height: ${ptCss(box.heightPt)}`)
  const insets = box.insetsPt
  if (insets) css.push(`padding: ${ptCss(insets.t)} ${ptCss(insets.r)} ${ptCss(insets.b)} ${ptCss(insets.l)}`)
  if (box.fill) css.push(`background-color: #${box.fill}`)
  if (box.line) css.push(`border: ${ptCss(Math.max(box.line.widthPt, 0.25))} solid #${box.line.color}`)
  const justify = { center: 'center', bottom: 'flex-end' }[box.verticalAlign]
  if (justify) css.push(`justify-content: ${justify}`)
  return { css: css.join('; '), absolute: placement.absolute }
}

/**
 * tblGrid → column widths in points for a <colgroup>, so the canvas keeps
 * Word's real column proportions under table-layout: fixed. Empty array
 * when the grid is absent or carries no usable widths.
 */
export function tblGridCols(tblGrid) {
  const root = parseFragment(tblGrid)
  if (!root) return []

  return memo(tblGridCache, tblGrid, () => {
    const cols = []
    for (const col of children(root)) {
      if (local(col) !== 'gridCol') continue
      const width = Number(attr(col, 'w'))
      cols.push(width > 0 ? twipsToPt(width) : 0)
    }
    return cols.some((width) => width > 0) ? cols : []
  })
}

/** EMU → CSS pixels (96dpi): 914400 EMU per inch. */
export function emuToPx(emu) {
  return Number(emu) > 0 ? Math.round(Number(emu) / 9525) : null
}

/** EMU → CSS pixels preserving sign — wp:posOffset may be negative. */
function emuToPxSigned(emu) {
  const value = Number(emu)
  return Number.isFinite(value) ? Math.round(value / 9525) : null
}

const anchorInfoCache = new Map()

/** wp:anchor wrap child → the display kind the canvas dispatches on. */
const WRAP_KINDS = {
  wrapSquare: 'square',
  wrapTight: 'tight',
  wrapThrough: 'tight', // through wraps around the contour like tight — same display treatment
  wrapNone: 'none',
  wrapTopAndBottom: 'topAndBottom',
}

/**
 * Anchored drawings mix wp/a/pic/r/wp14/… prefixes, and the stored
 * drawingXml carries NO xmlns declarations (the importer strips the
 * root-inherited ones for byte-clean passthrough) — parseFragment's
 * w-only wrapper cannot parse them. Auto-declare every prefix the fragment
 * uses with a placeholder URI instead of hard-coding the Word namespace
 * table: the traversal below is prefix-tolerant via local() and reads only
 * unprefixed attributes, so the URIs never matter — only parseability.
 */
function parseDrawingFragment(xml) {
  const prefixes = new Set()
  for (const match of xml.matchAll(/<([A-Za-z_][\w.-]*):/g)) prefixes.add(match[1])
  for (const match of xml.matchAll(/\s([A-Za-z_][\w.-]*):[A-Za-z_][\w.-]*\s*=/g)) prefixes.add(match[1])
  prefixes.delete('xml')
  prefixes.delete('xmlns')

  let declarations = ''
  for (const prefix of prefixes) declarations += ` xmlns:${prefix}="urn:x-studio-anchor:${prefix}"`

  try {
    domParser ??= new DOMParser()
    const doc = domParser.parseFromString(`<studio-root${declarations}>${xml}</studio-root>`, 'text/xml')
    return doc.querySelector('parsererror') ? null : doc.documentElement
  } catch {
    return null
  }
}

/** First wp:anchor / wp:inline frame in the drawing, breadth-first. */
function findDrawingFrame(root) {
  const queue = children(root)
  while (queue.length) {
    const element = queue.shift()
    const name = local(element)
    if (name === 'anchor' || name === 'inline') return element
    queue.push(...children(element))
  }
  return null
}

const inlinePictureCache = new Map()

/**
 * An inline picture (wp:inline) as Word sizes its line: the exact aspect of
 * its extent (the width/height attributes are whole pixels) and its
 * wp:effectExtent as margins — Word adds the effect extent to the picture's
 * room in the line (0.4 pt below a chart picture in the Tartu malls). ''
 * for anything else.
 */
export function inlinePictureCss(drawingXml, widthEmu, heightEmu) {
  if (!drawingXml || !(Number(widthEmu) > 0) || !(Number(heightEmu) > 0)) return ''
  return memo(inlinePictureCache, `${drawingXml}|${widthEmu}|${heightEmu}`, () => {
    const root = parseDrawingFragment(drawingXml)
    const frame = root ? findDrawingFrame(root) : null
    if (!frame || local(frame) !== 'inline') return ''
    const css = [`aspect-ratio: ${Number(widthEmu)} / ${Number(heightEmu)}`]
    const effect = children(frame).find((child) => local(child) === 'effectExtent')
    if (effect) {
      const pt = (name) => Math.round((Math.max(Number(effect.getAttribute(name)) || 0, 0) / 12700) * 100) / 100
      const [t, r, b, l] = ['t', 'r', 'b', 'l'].map(pt)
      if (t || r || b || l) css.push(`margin: ${t}pt ${r}pt ${b}pt ${l}pt`)
    }
    return css.join('; ')
  })
}

/** wp:positionH / wp:positionV → {relativeFrom, offsetPx|null, align|null}. */
function positionOf(frame, name) {
  const position = { relativeFrom: null, offsetPx: null, align: null }
  for (const child of children(frame)) {
    if (local(child) !== name) continue
    position.relativeFrom = child.getAttribute('relativeFrom') || null
    for (const part of children(child)) {
      if (local(part) === 'posOffset') position.offsetPx = emuToPxSigned(part.textContent)
      else if (local(part) === 'align') position.align = part.textContent.trim() || null
    }
    break
  }
  return position
}

/**
 * Anchor geometry of a verbatim w:drawing fragment, for the canvas's
 * floating-image rendering. Null when the fragment is unparsable or carries
 * no wp:anchor / wp:inline frame; wp:inline yields kind null (no wrap).
 *
 * @param {string|null} drawingXml verbatim <w:drawing>…</w:drawing>
 * @returns {null | {
 *   kind: 'square'|'tight'|'none'|'topAndBottom'|null,
 *   behindDoc: boolean,
 *   distPx: {t: number, r: number, b: number, l: number},
 *   posH: {relativeFrom: string|null, offsetPx: number|null, align: string|null},
 *   posV: {relativeFrom: string|null, offsetPx: number|null, align: string|null},
 * }}
 */
export function drawingAnchorInfo(drawingXml) {
  if (!drawingXml || typeof drawingXml !== 'string') return null

  return memo(anchorInfoCache, drawingXml, () => {
    const root = parseDrawingFragment(drawingXml)
    const frame = root ? findDrawingFrame(root) : null
    if (!frame) return null

    let kind = null
    for (const child of children(frame)) {
      const mapped = WRAP_KINDS[local(child)]
      if (mapped) {
        kind = mapped
        break
      }
    }

    // dist* / behindDoc are unprefixed attributes on the frame element.
    const behind = frame.getAttribute('behindDoc') || ''

    return {
      kind,
      behindDoc: behind !== '' && !isOff(behind),
      distPx: {
        t: emuToPx(frame.getAttribute('distT')) ?? 0,
        r: emuToPx(frame.getAttribute('distR')) ?? 0,
        b: emuToPx(frame.getAttribute('distB')) ?? 0,
        l: emuToPx(frame.getAttribute('distL')) ?? 0,
      },
      posH: positionOf(frame, 'positionH'),
      posV: positionOf(frame, 'positionV'),
    }
  })
}

const rStyleCache = new Map()
const tblStyleCache = new Map()

const toggleOffCache = new Map()

/**
 * Classes for the toggles an rPr switches off explicitly (`<w:b w:val="0"/>`,
 * `<w:i w:val="0"/>`): direct formatting that beats bold or italic from a
 * style, e.g. a table style's first column (studio.css). A toggle that is on
 * is a mark of its own (Bold, Italic), so only "off" is carried here.
 */
export function runToggleOffClasses(rPr) {
  const root = parseFragment(rPr)
  if (!root) return ''

  return memo(toggleOffCache, rPr, () => {
    const classes = []
    for (const property of children(root)) {
      const name = local(property)
      if ((name === 'b' || name === 'i') && isOff(attr(property, 'val'))) classes.push(`studio-run-${name}-off`)
    }
    return classes.join(' ')
  })
}

/** Character style id referenced by an rPr's w:rStyle, or null. */
export function rStyleOf(rPr) {
  const root = parseFragment(rPr)
  if (!root) return null

  return memo(rStyleCache, rPr, () => {
    for (const property of children(root)) {
      if (local(property) === 'rStyle') {
        const value = attr(property, 'val')
        return value || null
      }
    }
    return null
  })
}

/** Legacy tblLook bitmask bits (pre-transitional attribute form). */
const TBL_LOOK_BITS = {
  firstRow: 0x20,
  lastRow: 0x40,
  firstColumn: 0x80,
  lastColumn: 0x100,
  noHBand: 0x200,
  noVBand: 0x400,
}

/**
 * Table-style binding for a tblPr: the referenced style id plus the tblLook
 * conditional-formatting switches (attribute form first, legacy val bitmask
 * as fallback). Null when the table binds no style.
 */
export function tblStyleInfo(tblPr) {
  const root = parseFragment(tblPr)
  if (!root) return null

  return memo(tblStyleCache, tblPr, () => {
    let styleId = null
    const look = {
      firstRow: false,
      lastRow: false,
      firstColumn: false,
      lastColumn: false,
      noHBand: false,
      noVBand: false,
    }

    for (const property of children(root)) {
      const name = local(property)
      if (name === 'tblStyle') {
        styleId = attr(property, 'val') || null
      } else if (name === 'tblLook') {
        const mask = parseInt(attr(property, 'val') || '0', 16)
        for (const key of Object.keys(look)) {
          const flag = attr(property, key)
          look[key] = flag !== '' ? !isOff(flag) : (mask & TBL_LOOK_BITS[key]) !== 0
        }
      }
    }

    return styleId ? { styleId, look } : null
  })
}

const indCache = new Map()

/**
 * A pPr's direct indentation in twips, or null when the paragraph declares
 * none — direct ind overrides the numbering level's geometry (Word rule).
 */
export function indOf(pPr) {
  const root = parseFragment(pPr)
  if (!root) return null

  return memo(indCache, pPr, () => {
    for (const property of children(root)) {
      if (local(property) !== 'ind') continue
      const read = (name, alt) => {
        const value = attr(property, name) || (alt ? attr(property, alt) : '')
        return value === '' ? null : Number(value)
      }
      return {
        leftTw: read('left', 'start'),
        hangingTw: read('hanging'),
        firstLineTw: read('firstLine'),
      }
    }
    return null
  })
}

const paginationHintsCache = new Map()

const NO_PAGINATION_HINTS = Object.freeze({
  pageBreakBefore: false,
  keepNext: false,
  keepLines: false,
})

/**
 * A pPr's pagination hints for the page-fitting engine: w:pageBreakBefore,
 * w:keepNext, w:keepLines. Each is true when the element is present and its
 * val (if any) is not an off value — Word's on/off semantics.
 */
export function pPrPaginationHints(pPr) {
  const root = parseFragment(pPr)
  if (!root) return NO_PAGINATION_HINTS

  return memo(paginationHintsCache, pPr, () => {
    const hints = { ...NO_PAGINATION_HINTS }

    for (const property of children(root)) {
      const name = local(property)
      if (name !== 'pageBreakBefore' && name !== 'keepNext' && name !== 'keepLines') continue
      const value = attr(property, 'val')
      hints[name] = value === '' || !isOff(value)
    }

    return hints
  })
}

const trPrPaginationHintsCache = new Map()

const NO_TR_PAGINATION_HINTS = Object.freeze({
  tblHeader: false,
  cantSplit: false,
})

/**
 * A trPr's pagination hints for the page-fitting engine: w:tblHeader
 * (repeat-this-row-as-header) and w:cantSplit. Same OOXML on/off semantics
 * as pPrPaginationHints — a bare element (or truthy val) is on, val
 * "0"/"false" is off. Null/unparsable input yields a frozen all-false
 * default.
 *
 * cantSplit is inherently honored — rows are already atomic breakables that
 * never split across pages — so it is parsed for completeness/documentation
 * only and maps to no behaviour.
 */
export function trPrPaginationHints(trPr) {
  const root = parseFragment(trPr)
  if (!root) return NO_TR_PAGINATION_HINTS

  return memo(trPrPaginationHintsCache, trPr, () => {
    const hints = { ...NO_TR_PAGINATION_HINTS }

    for (const property of children(root)) {
      const name = local(property)
      if (name !== 'tblHeader' && name !== 'cantSplit') continue
      const value = attr(property, 'val')
      hints[name] = value === '' || !isOff(value)
    }

    return hints
  })
}

const contextualCache = new Map()

/**
 * A pPr's own w:contextualSpacing: '1' (on), '0' (switched off against its
 * style) or null (not set). The space between two paragraphs of one style is
 * dropped from the side whose paragraph has it (studioStylesheets.js).
 */
export function contextualSpacingOf(pPr) {
  const root = parseFragment(pPr)
  if (!root) return null
  return memo(contextualCache, pPr, () => {
    const property = children(root).find((child) => local(child) === 'contextualSpacing')
    if (!property) return null
    return isOff(attr(property, 'val')) ? '0' : '1'
  })
}

const trHeightCache = new Map()

const gridBeforeCache = new Map()

/**
 * The grid columns a row skips before its first cell (w:gridBefore), 0
 * when it skips none: the row's first cell sits at that grid column.
 */
export function gridBeforeOf(trPr) {
  const root = parseFragment(trPr)
  if (!root) return 0
  return memo(gridBeforeCache, trPr, () => {
    const gridBefore = children(root).find((property) => local(property) === 'gridBefore')
    const columns = gridBefore ? Number(attr(gridBefore, 'val')) : 0
    return Number.isInteger(columns) && columns > 0 ? columns : 0
  })
}

/**
 * A row's w:trHeight as CSS: a table row's height is its minimum, which is
 * Word's default rule (atLeast); an exact row is drawn at least that tall too.
 * '' without one.
 */
export function trHeightCss(trPr) {
  const root = parseFragment(trPr)
  if (!root) return ''
  return memo(trHeightCache, trPr, () => {
    const height = children(root).find((property) => local(property) === 'trHeight')
    const value = height ? Number(attr(height, 'val')) : 0
    return value > 0 && attr(height, 'hRule') !== 'auto' ? `height: ${twipsToPt(value)}pt` : ''
  })
}

/**
 * A table the editor created (tblPr and tblGrid both null, contract §4): the
 * compiler gives it the full text width and single ½ pt borders on all six
 * edges (or the fragment's "Table Grid" style, which draws the same), so the
 * canvas publishes those as the studio.css custom properties.
 */
export const NEW_TABLE_CSS = [
  ...['top', 'right', 'bottom', 'left', 'insideH', 'insideV'].map((edge) => `--sb-${edge}: 0.5pt solid rgb(0, 0, 0)`),
  '--sb-width: 100%',
].join('; ')

/** The w:val of a fragment's direct child element, or null. */
function childVal(fragment, name) {
  for (const property of children(parseFragment(fragment))) {
    if (local(property) === name) return attr(property, 'val') || null
  }
  return null
}

/**
 * Toolbar state: the text colour an ooxmlRun mark's attrs show — the colour
 * delta, else the rPr's w:color ('auto' or upper-case 6-hex), else null (the
 * style decides).
 */
export function runColor(attrs) {
  return normalizeRunColor(attrs?.color ?? null) ?? normalizeRunColor(childVal(attrs?.rPr, 'color'))
}

/**
 * Toolbar state: the highlight an ooxmlRun mark's attrs show — the highlight
 * delta ('none' included), else the rPr's w:highlight name, else null.
 */
export function runHighlight(attrs) {
  const delta = attrs?.highlight ?? null
  if (delta === 'none' || RUN_HIGHLIGHTS.includes(delta)) return delta
  const value = childVal(attrs?.rPr, 'highlight')
  return RUN_HIGHLIGHTS.includes(value) ? value : null
}

/**
 * Toolbar state: a paragraph's or heading's alignment — the textAlign delta,
 * else what its pPr's w:jc says (start/end/distribute fold in), else null
 * (the paragraph style decides).
 */
export function paragraphAlign(attrs) {
  const delta = attrs?.textAlign ?? null
  if (PARAGRAPH_ALIGNMENTS.includes(delta)) return delta
  return JC_ALIGNMENTS[childVal(attrs?.pPr, 'jc')] ?? null
}
